/** @jsxImportSource @emotion/react */
import React, { useState, useMemo, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { addClass, hasClass, DataType } from '@grapecity/wijmo';
import { CellType } from '@grapecity/wijmo.grid';
import { Button } from '@mui/material';
import {
  SearchBox,
  SearchBoxRow,
  InputBox,
  SearchRows,
  SearchCols,
  SearchButtonWrap,
} from 'components/layouts/SearchBox';
import { uploadExcelTemplates } from 'apis/common/Excel';
import CustomInputWithSearch from 'components/inputs/CustomInputWithSearch';
import { useMessageBar } from 'hooks/useMessageBar';
import { useCommonModal } from 'hooks/useCommonModal';
import SearchBoxButtons from 'components/buttons/SearchBoxButtons';
import { useLoading } from 'components/process/Loading';
import { getCommonCodeNames, getCommonCodeNamesCondition } from 'apis/admin/CommonCode';
import CustomGrid from 'components/grids/CustomGrid';
import {
  ControlBtnGroup,
  GlobalBtnGroup,
  SubTitleGroup,
  SubTitleLayout,
} from 'components/layouts/ContentLayout';
import { IconButton } from 'components/buttons/IconSVG';
import { MdResultCondition, MdResultMaster, MdResultDetail } from 'models/md/MdResult';
import { CommonUtil } from 'utils/CommonUtil';
import { findMdResults, deleteMdResultsDetailList } from 'apis/md/MdResult';
import { MultiComboBox } from 'components/selects/ComboBox';
import { ManagementMode } from 'models/common/Common';
import { useNavigate, useLocation } from 'react-router-dom';
import { PageMode } from 'models/common/Common';
import { downloadExcelTemplatesPost } from 'apis/common/Excel';
import { FileTypeName } from 'models/common/Common';
import ExcelUploadButton from 'components/buttons/ExcelUploadButton';
import ExcelValidModal from 'components/modals/common/ExcelValidModal';
import { hasRole } from 'utils/SessionUtil';

/**
 * @desc 투자비관리 > 형개발 투자비 산출 > 투자이력 Database
 * @author
 * -------------------------------------------------------------------------------------
 *  Date.       author         Description
 * -------------------------------------------------------------------------------------
 * 2026.05.13   jshwang12       신규 생성
 */
const MdIvesHisInqPage = () => {
  const gridRef = useRef<any>();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const { openLoading } = useLoading();
  const { openMessageBar } = useMessageBar();
  const { openCommonModal } = useCommonModal();
  const [code, setCode] = useState<any>();
  const initCondition = {
    prdnProcCds: [],
    eqclIds: [],
  };
  const [condition, setCondition] = useState<MdResultCondition>(initCondition);

  const [checkedItems, setCheckedItems] = useState<MdResultMaster[]>();
  const [rowData, setRowData] = useState<MdResultDetail[]>([]);
  const [excelValidCondition, setExcelValidCondition] = useState<string | null>(null);
  const [isExcelValidModalOpen, setExcelValidModalOpen] = useState<boolean>(false);
  const [hasAuth, setHasAuth] = useState<boolean>(false);

  useEffect(() => {
    getCommonCodes();
    handleSearch();
  }, []);

  useEffect(() => {
    if (hasRole('MD_IVES_MANAGER') || hasRole('ADM')) {
      setHasAuth(true);
    }
  }, []);

  const mapDisplayStacking = useMemo(() => {
    return Object.fromEntries((code?.mdStackingTp || []).map((o) => [o.cmnCd, o.optValCtn1]));
  }, [code?.mdStackingTp]);

  const getCommonCodes = () => {
    Promise.all([
      getCommonCodeNames('MD_EQCL_ID'), // 설비군
      getCommonCodeNames('MD_STACKING_TP'), // Stacking Type
      getCommonCodeNamesCondition({
        cmnGrCd: 'PRDN_PROC_CD',
        optValCtn7: 'Y',
      }),
    ]).then(([mdEqclId, mdStackingTp, prdnProcCd]) => {
      setCode({
        mdEqclId: mdEqclId,
        mdStackingTp: mdStackingTp,
        prdnProcCd: prdnProcCd,
      });
    });
  };

  const handleOnChange = (name: string, value: any) => {
    setCondition((prev) => ({ ...prev, [name]: value }));
  };

  /**
   * 조회
   */
  const handleSearch = () => {
    findMdResults(condition).then((result) => {
      setRowData(result);
    });
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleDownloadExcel = () => {
    CommonUtil.exportWijmoExcelGrid(
      gridRef.current,
      t('md.label.투자이력 Database 목록', '투자이력 Database 목록'),
      t('md.label.투자이력 Database 목록', '투자이력 Database 목록')
    );
  };

  const downloadExcelTemplate = () => {
    openLoading(true);
    downloadExcelTemplatesPost(
      FileTypeName.TPL_MD_IVES_DB,
      '',
      [],
      condition?.wbsCd,
      condition?.wbsCdNm,
      condition?.mdlNm,
      condition?.fertHircStrcNm,
      (condition?.prdnProcCds || []).join(','),
      (condition?.eqclIds || []).join(','),
      condition?.ivesAtcNm,
      condition?.ivesDtlDesc
    ).finally(() => openLoading(false));
  };

  const layoutDefinition = useMemo(() => {
    return [
      {
        binding: 'no',
        header: String(t('com.label.NO', 'NO')),
        width: 40,
        minWidth: 40,
        align: 'center',
        cellTemplate: (grid) => grid.row._idx + 1,
      },
      {
        binding: 'mdlNm',
        header: String(t('md.label.모델', '모델')),
        width: 70,
        minWidth: 50,
        align: 'center',
      },
      {
        binding: 'bildPlntCdNm',
        header: String(t('md.label.Plant', 'Plant')),
        width: 80,
        minWidth: 50,
        align: 'center',
      },
      {
        binding: 'prdnLnNm',
        header: String(t('md.label.호기', '호기')),
        width: 100,
        minWidth: 80,
        align: 'center',
      },
      {
        binding: 'fertHircStrcCdNm',
        header: String(t('md.label.조성', '조성')),
        width: 100,
        minWidth: 80,
        align: 'center',
      },
      {
        binding: 'stakTpCdNm',
        header: String(t('md.label.Stacking Type', 'Stacking Type')),
        width: 124,
        minWidth: 50,
        align: 'center',
      },
      {
        binding: 'cellTpCdNm',
        header: String(t('md.label.셀 Type', '셀 Type')),
        width: 90,
        minWidth: 50,
        align: 'center',
      },
      {
        binding: 'rprsSpecCdNm',
        header: String(t('md.label.압연사양', '압연사양')),
        width: 90,
        minWidth: 50,
        align: 'center',
      },
      {
        binding: 'stakFoldQty',
        header: String(t('md.label.Stack', 'Stack')),
        width: 40,
        minWidth: 40,
        align: 'center',
      },
      {
        binding: 'stackDisplay',
        header: String(t('md.label.Stack', 'Stack')),
        width: 40,
        minWidth: 40,
        align: 'center',
        cellTemplate: (params) => {
          return mapDisplayStacking?.[params.item?.stakTpCd || ''] || '';
        },
      },
      {
        binding: 'prdnProcCdNm',
        header: String(t('md.label.공정', '공정')),
        width: 80,
        minWidth: 50,
        align: 'center',
      },
      {
        binding: 'eqclNm',
        header: String(t('md.label.설비', '설비')),
        width: 100,
        minWidth: 80,
        align: 'center',
      },
      {
        binding: 'stndYnNm',
        header: String(t('md.label.구분', '구분')),
        width: 80,
        minWidth: 50,
        align: 'center',
      },
      {
        binding: 'ivesAtcNm',
        header: String(t('md.label.투자항목', '투자항목')),
        width: 100,
        minWidth: 80,
        align: 'left',
      },
      {
        binding: 'ivesDtlDesc',
        header: String(t('md.label.투자상세', '투자상세')),
        width: '*',
        minWidth: 250,
        align: 'left',
      },
      {
        binding: 'quotUprc',
        header: String(t('md.label.단가', '단가')),
        width: 80,
        minWidth: 50,
        align: 'right',
        dataType: DataType.String,
      },
      {
        binding: 'uprcDcnCtn',
        header: String(t('md.label.단가기준', '단가기준')),
        width: 100,
        minWidth: 80,
        align: 'left',
      },
      {
        binding: 'chkCtn',
        header: String(t('md.label.확인 필요', '확인 필요')),
        width: 100,
        minWidth: 80,
        align: 'left',
      },
      {
        binding: 'wbsCd',
        header: String(t('md.label.WBS', 'WBS')),
        width: 110,
        minWidth: 80,
        align: 'center',
      },
      {
        binding: 'cnsnAprvDt',
        header: String(t('md.label.품의일자', '품의일자')),
        width: 90,
        minWidth: 80,
        align: 'center',
      },
      {
        binding: 'wbsCdNm',
        header: String(t('md.label.투자명', '투자명')),
        width: 150,
        minWidth: 80,
        align: 'left',
      },
      {
        binding: 'fertMdlDesc',
        header: String(t('md.label.기존 양산 모델', '기존 양산 모델')),
        width: 130,
        minWidth: 80,
        align: 'left',
      },
    ];
  }, [mapDisplayStacking]);

  const onInitialized = (grid) => {
    gridRef.current = grid;

    /*grid.formatItem.addHandler((s, e) => {
      const item = e.panel.rows[e.row]?.dataItem;
      if (e.panel.cellType === CellType.TopLeft && e.cell.querySelector('input').indeterminate) {
        e.cell.querySelector('input').checked = true;
      }
      if (e.panel === s.rowHeaders) {
        if (e.cell.hasChildNodes()) {
          // 이미 사용중인 항목인 경우 선택불가/readonly
          if (item?.usedYn === 'Y') {
            addClass(e.cell, 'wj-state-disabled');
          }
        }
      }
    });*/

    /*grid.hostElement.addEventListener('click', (e) => {
      // 전체 선택 시
      if (hasClass(e.target, 'wj-column-selector-group') && e.target.checked) {
        grid.deferUpdate(() => {
          grid.rows.forEach((row) => {
            if (row?.dataItem?.usedYn === 'Y') {
              row.isSelected = false;
            }
          });
        });
      }
    });*/
  };

  return (
    <>
      <SearchBox>
        <SearchBoxRow>
          <InputBox>
            <SearchRows className="FourCol">
              <SearchCols>
                <label>{String(t('md.label.WBS', 'WBS'))}</label>
                <CustomInputWithSearch
                  name="wbsCd"
                  value={condition?.wbsCd}
                  placeholder={String(t('md.label.WBS를 입력해 주세요.', 'WBS를 입력해 주세요.'))}
                  onChange={(e) => handleOnChange(e.target.name, e.target.value)}
                  onKeyDown={handleKeyDown}
                />
              </SearchCols>
              <SearchCols>
                <label>{String(t('md.label.투자명', '투자명'))}</label>
                <CustomInputWithSearch
                  name="wbsCdNm"
                  value={condition?.wbsCdNm}
                  placeholder={String(
                    t('md.label.투자명을 입력해 주세요.', '투자명을 입력해 주세요.')
                  )}
                  onChange={(e) => handleOnChange(e.target.name, e.target.value)}
                  onKeyDown={handleKeyDown}
                />
              </SearchCols>
              <SearchCols>
                <label>{t('md.label.모델', '모델')}</label>
                <CustomInputWithSearch
                  name="mdlNm"
                  value={condition?.mdlNm}
                  placeholder={String(t('md.label.모델을 입력해 주세요.', '모델을 입력해 주세요.'))}
                  onChange={(e) => handleOnChange(e.target.name, e.target.value)}
                  onKeyDown={handleKeyDown}
                />
              </SearchCols>
              <SearchCols>
                <label>{t('md.label.조성', '조성')}</label>
                <CustomInputWithSearch
                  name="fertHircStrcNm"
                  value={condition?.fertHircStrcNm}
                  placeholder={String(t('md.label.조성을 입력해 주세요.', '조성을 입력해 주세요.'))}
                  onChange={(e) => handleOnChange(e.target.name, e.target.value)}
                  onKeyDown={handleKeyDown}
                />
              </SearchCols>
            </SearchRows>
            <SearchRows className="FourCol">
              <SearchCols>
                <label>{t('md.label.공정', '공정')}</label>
                <MultiComboBox
                  id="prdnProcCd"
                  options={code?.prdnProcCd || []}
                  placeholder={String(t('com.label.전체', '전체'))}
                  defaultValue={condition?.prdnProcCds || []}
                  onChange={(value) => handleOnChange('prdnProcCds', value)}
                />
              </SearchCols>
              <SearchCols>
                <label>{t('md.label.설비', '설비')}</label>
                <MultiComboBox
                  id="eqclId"
                  options={code?.mdEqclId || []}
                  placeholder={String(t('com.label.전체', '전체'))}
                  defaultValue={condition?.eqclIds || []}
                  onChange={(value) => handleOnChange('eqclIds', value)}
                />
              </SearchCols>
              <SearchCols>
                <label>{t('md.label.투자항목', '투자항목')}</label>
                <CustomInputWithSearch
                  name="ivesAtcNm"
                  value={condition?.ivesAtcNm}
                  placeholder={String(
                    t('md.label.투자항목을 입력해 주세요.', '투자항목을 입력해 주세요.')
                  )}
                  onChange={(e) => handleOnChange(e.target.name, e.target.value)}
                  onKeyDown={handleKeyDown}
                />
              </SearchCols>
              <SearchCols>
                <label>{t('md.label.투자상세', '투자상세')}</label>
                <CustomInputWithSearch
                  name="ivesDtlDesc"
                  value={condition?.ivesDtlDesc}
                  placeholder={String(
                    t('md.label.투자상세를 입력해 주세요.', '투자상세를 입력해 주세요.')
                  )}
                  onChange={(e) => handleOnChange(e.target.name, e.target.value)}
                  onKeyDown={handleKeyDown}
                />
              </SearchCols>
            </SearchRows>
          </InputBox>
          <SearchButtonWrap>
            <SearchBoxButtons
              defaultCondition={initCondition}
              setReset={setCondition}
              onSearch={() => handleSearch()}
            />
          </SearchButtonWrap>
        </SearchBoxRow>
      </SearchBox>
      <SubTitleLayout>
        <SubTitleGroup>
          <h3>{t('md.label.투자이력 Database 목록', '투자이력 Database 목록')}</h3>
          <span className="total">
            {t('com.label.총', '총')}
            <span>{(rowData || []).length.toLocaleString()}</span>
            {t('com.label.건', '건')}
          </span>
        </SubTitleGroup>
        <ControlBtnGroup>
          {hasAuth && (
            <>
              <ExcelUploadButton
                msgConfirm={
                  t(
                    'md.label.엑셀 양식에 작성된 내용으로 추가/수정됩니다.<br/>새로 Upload 하시겠습니까?',
                    '엑셀 양식에 작성된 내용으로 추가/수정됩니다.<br/>새로 Upload 하시겠습니까?'
                  ) || ''
                }
                onCallback={(file) => {
                  openLoading(true);
                  uploadExcelTemplates(
                    file,
                    'TPL_MD_IVES_DB',
                    'NORMAL',
                    'TB_EELMB_MD_RLT_M',
                    'RLT' // 현재 id값 미사용이지만 필수값이라 임의문자열 보냄
                  )
                    .then((response) => {
                      if (response?.data.x_result == 'OK') {
                        openMessageBar({
                          type: 'confirm',
                          content: t('md.label.검증이 완료되었습니다.', '검증이 완료되었습니다.'),
                        });
                        handleSearch();
                      } else {
                        setExcelValidCondition(response?.data.p_xls_upload_id);
                        setExcelValidModalOpen(true);
                      }
                    })
                    .finally(() => {
                      openLoading(false);
                    });
                }}
              />
              <Button
                css={IconButton.button}
                className="Exceldown"
                onClick={() => downloadExcelTemplate()}
                disableRipple
              >
                {t('com.button.템플릿 다운로드', '템플릿 다운로드')}
              </Button>
            </>
          )}
          <Button
            css={IconButton.button}
            className="Exceldown"
            onClick={handleDownloadExcel}
            disableRipple
          >
            {t('com.button.다운로드', '다운로드')}
          </Button>
          <Button
            css={IconButton.button}
            className="refresh"
            onClick={() => handleSearch()}
            disableRipple
          >
            {t('com.button.새로고침', '새로고침')}
          </Button>
        </ControlBtnGroup>
      </SubTitleLayout>
      <CustomGrid
        layoutDefinition={layoutDefinition}
        rowData={rowData}
        height={548}
        frozenColumns={2}
        isReadOnly={true}
        autoCheck={true}
        initialized={onInitialized}
        onChangeCheckedItem={(items) => setCheckedItems(items)}
      />
      {hasAuth && (
        <GlobalBtnGroup>
          <Button
            css={IconButton.button}
            className="delete"
            onClick={() => {
              const items = gridRef.current.rows.filter((item) => item.isSelected);
              if (items.length < 1) {
                openMessageBar({
                  type: 'error',
                  content: t('md.label.삭제할 행을 선택해 주세요.', '삭제할 행을 선택해 주세요.'),
                });
                return false;
              } else {
                const deleteData = items.map((row) => row.dataItem);
                if (deleteData.filter((o) => o.usedYn === 'Y').length > 0) {
                  openMessageBar({
                    type: 'error',
                    content: t(
                      'md.label.투자모델에서 사용중인 항목은 삭제할 수 없습니다.',
                      '투자모델에서 사용중인 항목은 삭제할 수 없습니다.'
                    ),
                  });
                  return false;
                }
                openCommonModal({
                  modalType: 'confirm',
                  content: t(`com.msg.삭제하시겠습니까?`, `삭제하시겠습니까?`),
                  yesCallback: () => {
                    deleteMdResultsDetailList(deleteData).then((result) => {
                      if (result?.successOrNot === 'Y') {
                        openMessageBar({
                          type: 'confirm',
                          content: t('com.label.삭제되었습니다.', '삭제되었습니다.'),
                        });
                        handleSearch();
                      }
                    });
                    gridRef.current.refresh();
                  },
                });
              }
            }}
            disableRipple
          >
            {t('com.button.삭제', '삭제')}
          </Button>
          <Button
            css={IconButton.button}
            className="edit"
            onClick={() => {
              if (checkedItems && checkedItems.length > 0) {
                if (checkedItems.length > 1) {
                  openMessageBar({
                    type: 'error',
                    content: t(
                      'ip.msg.수정할 대상을 1건만 선택해 주세요.',
                      '수정할 대상을 1건만 선택해 주세요.'
                    ),
                  });
                  return;
                }

                const searchPageCondition = {
                  mode: PageMode.EDIT,
                  upprMnuUrl: location.pathname,
                  mnuId: `${location.pathname}/${ManagementMode.MODIFY}`,
                  mnuNm: t('md.label.투자이력 Database 수정', '투자이력 Database 수정'),
                  mdRltId: checkedItems[0].mdRltId,
                  wbsCd: checkedItems[0].wbsCd,
                };
                navigate(
                  `/md/invest-history/detail?mdRltId=${checkedItems[0].mdRltId}&wbsCd=${checkedItems[0].wbsCd}`,
                  {
                    state: { ...searchPageCondition },
                  }
                );
              } else {
                openMessageBar({
                  type: 'error',
                  content: t('md.msg.수정할 대상을 선택해 주세요.', '수정할 대상을 선택해 주세요.'),
                });
              }
            }}
            disableRipple
          >
            {t('com.button.수정', '수정')}
          </Button>
          <Button
            css={IconButton.button}
            className="write"
            onClick={() => {
              navigate('/md/invest-history/new', {
                state: {
                  upprMnuUrl: location.pathname,
                  mode: PageMode.WRITE,
                  mnuId: `${location.pathname}/${PageMode.WRITE}`,
                  mnuNm: t('md.label.투자이력 Database 등록', '투자이력 Database 등록'),
                },
              });
            }}
            disableRipple
          >
            {t('com.button.신규', '신규')}
          </Button>
        </GlobalBtnGroup>
      )}

      {isExcelValidModalOpen && (
        <ExcelValidModal
          open={isExcelValidModalOpen}
          close={() => {
            setExcelValidCondition(null);
            setExcelValidModalOpen(false);
          }}
          condition={{
            valReqId: excelValidCondition || '',
          }}
        />
      )}
    </>
  );
};

export default MdIvesHisInqPage;
